import _ from 'underscore';
import { useEffect, useContext, useCallback, useState, useMemo } from 'react';
import { edmCampaignSetup } from '@bingads-webui/edm-campaign';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { useSignUp } from '@bingads-webui/overreact-cc';
import { usePromisify } from '@bingads-webui-react/overreact';
import { initLegacyOdata } from '@bingads-webui-campaign-react/pmax-lite-wizard-utils';
import { isSDK } from '@bingads-webui-campaign-react/sdk-utils';
import { InstrumentationServiceContext, setupGlobal, SUB_SYSTEM_NAMES } from '@bingads-webui-campaign-react/pmax-lite-instrumentation';
import { ServerContext, useUpdateServerContext } from '@bingads-webui-campaign-react/server-context';
import { usePostponeReadVariables } from '@bingads-webui-campaign-react/basic-context-utils';
import { PMAX_TEMP_NAME_PREFIX } from '@bingads-webui-campaign/campaign-ui-common';

// Custom hook that doesn't re-run when "scope" object changes
// This object changes too often, messing up our "useEffects"
function useScopeEffect(callback) {
  const { scope } = useContext(ScopeContext);

  useEffect(() => {
    callback(scope);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callback]);
}

export function useCreateUser({
  devConfig = {},
  subSystemName = SUB_SYSTEM_NAMES.PMaxLiteSignup,
  tempPrefix = PMAX_TEMP_NAME_PREFIX,
  authType,
  uiActivity,
  userInfo = {},
  isChinaSignup = false,
  onUserCreationStart = _.noop,
  onUserCreationComplete = _.noop,
  onUserCreationError = _.noop,
}) {
  const [, setInstrumentationService] = (useContext(InstrumentationServiceContext) as any);
  const updateServerContextFn = useUpdateServerContext();
  const [localServerContext, setLocalServerContext] = useState<any>(null);

  const [error, setError] = useState();

  useEffect(() => {
    if (!_.isEmpty(error)) {
      // Throw error here because error boundaries don't catch errors thrown on async code https://reactjs.org/docs/error-boundaries.html
      throw new Error(error);
    }
  }, [error]);

  const fetchConfig = usePostponeReadVariables();
  const variables = useMemo(() => ({
    locator: {
      descriptor: {},
    },
    isPMaxLite: true,
    isChinaSignup,
    authType,
  }), [authType, isChinaSignup]);

  const [, { read: signUp }] = useSignUp(variables, fetchConfig);

  const [signUpAsync] = usePromisify([signUp]);

  const onSignUpError = useCallback((response) => {
    if (response.RedirectUrl) {
      window.location.replace(response.RedirectUrl);
      return;
    }
    if (response.Errors && response.Errors.length > 0) {
      setError(response.Errors[0].Code);
    }
    onUserCreationError(response);
  }, [onUserCreationError]);
  const serverContext = useContext(ServerContext) as any;

  const onSignUpComplete = useCallback((response) => {
    if (response.ResultType === 2) {
      return Promise.reject(response);
    }
    // For SDK instead of following server side configs, a static permission map is used.
    // Here we need to override the permissions with the objects passed from wrapper component.
    const newServerContext = updateServerContextFn(response, uiActivity, isSDK() ? {
      Permissions: serverContext.Permissions,
      DynamicPermissions: serverContext.DynamicPermissions,
      CCUIPilots: serverContext.CCUIPilots,
    } : {});

    const instrumentation = setupGlobal({
      serverContext: newServerContext,
      setInstrumentationService,
      devConfig,
      subSystemName,
    });
    // use the newest instrumentation to log
    const scenario = instrumentation.pmaxLiteSignup.create();
    const activity = scenario.userCreation.create();
    activity.trace('[PMaxLiteSignup] User creation complete');

    (window as any).campaignODataConfigs = {
      AdvertiserCustomerID: newServerContext.CurrentAccount.AdvertiserCustomerID,
      AccountID: newServerContext.CurrentAccount.Id,
      Token: newServerContext.Token,
      ServiceRoot: `${newServerContext.ServicesEndpoints.CampaignAPI}${newServerContext.CampaignAPIPath}`,
    };
    initLegacyOdata(newServerContext);

    setLocalServerContext(newServerContext);

    onUserCreationComplete(newServerContext);
    return response;
  }, [
    devConfig,
    onUserCreationComplete,
    serverContext.CCUIPilots,
    serverContext.DynamicPermissions,
    serverContext.Permissions,
    setInstrumentationService,
    subSystemName,
    uiActivity,
    updateServerContextFn,
  ]);

  useScopeEffect(useCallback((scope) => {
    if (localServerContext) {
      scope.append({
        uid: localServerContext.CurrentUser.Id,
        cid: localServerContext.CurrentCustomer.Id,
        aid: localServerContext.CurrentAccount.Id,
      });
      scope.save(undefined, undefined, { replace: true });
    }
  }, [localServerContext]));

  useEffect(() => {
    if (localServerContext && (window as any).campaignODataConfigs && (window as any).campaignODataConfigs.AccountID) {
      edmCampaignSetup();
    }
  }, [localServerContext]);

  const createUser = useCallback((newUserInfo = {}) => {
    onUserCreationStart();

    return signUpAsync({
      data: {
        ...userInfo,
        ...newUserInfo,
        businessName: `${tempPrefix}${Date.now()}`,
        isExpert: true,
        idp: authType,
      },
    })
      .then(account => onSignUpComplete(account))
      .catch((err) => {
        onSignUpError(err);
        return Promise.reject(err);
      });
  }, [authType, onSignUpComplete, onSignUpError, onUserCreationStart, signUpAsync, tempPrefix, userInfo]);

  return createUser;
}
